/**
 * Accessibility remediation - "CC Pops" size-chart popup (audit finding #100).
 *
 * The size-chart trigger and its popup are injected at runtime by a third-party
 * app, so none of this markup exists in the theme and none of it can be fixed
 * in Liquid. The app gives us:
 *
 *   a.ccpops-trigger[href="javascript:void(0)"]   - announced as a link, no state
 *     span.ccpops-trigger__text                   - "size chart"
 *   #ccpops-popup.ccpops-veil                     - the modal, toggled by an
 *     .ccpops-popup-container                       inline style display/opacity
 *       .ccpops-popup-container__close              (no role, no name, no trap)
 *
 * Measured defects this script fixes:
 *   - the trigger is an <a> with no role="button", no aria-haspopup, no
 *     aria-expanded, and no Space-key activation;
 *   - the popup has no role="dialog", no aria-modal and no accessible name;
 *   - opening it leaves focus on the trigger, so a screen reader user is never
 *     moved into the dialog;
 *   - Tab is not trapped, so focus walks out into the page behind the veil;
 *   - Escape does not close it;
 *   - focus is not restored to the trigger on close.
 *
 * The closed state is already correct - the veil is display:none, so nothing
 * inside is reachable - and is deliberately left alone.
 *
 * Patches are idempotent, keyed off a data-attribute marker, and re-applied by
 * a MutationObserver because the app re-injects its markup per product page.
 */
(function () {
  'use strict';

  var MARK = 'data-ccpops-a11y';
  var VEIL_ID = 'ccpops-popup';
  var TRIGGER_SELECTOR = 'a.ccpops-trigger, .ccpops-trigger';
  var CLOSE_SELECTOR = '.ccpops-popup-container__close';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var lastTrigger = null;
  var wasOpen = false;

  function marked(el, key) {
    if (!el || el.getAttribute(MARK) === key) return true;
    el.setAttribute(MARK, key);
    return false;
  }

  function veil() {
    return document.getElementById(VEIL_ID);
  }

  function isOpen(el) {
    return !!el && window.getComputedStyle(el).display !== 'none';
  }

  function visibleFocusables(root) {
    return [].slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.getBoundingClientRect().height > 0;
    });
  }

  /* ---------------------------------------------------------------- trigger */

  function patchTrigger(trigger) {
    if (marked(trigger, 'trigger')) return;

    // The app ships an <a href="javascript:void(0)">, which is announced as a
    // link even though it opens a dialog. role="button" corrects the role
    // without touching the app's own click handler.
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');

    // Links activate on Enter but not Space; a button must do both.
    trigger.addEventListener('keydown', function (event) {
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        trigger.click();
      }
    });

    trigger.addEventListener('click', function () {
      lastTrigger = trigger;
    });
  }

  function setExpanded(state) {
    var triggers = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', state ? 'true' : 'false');
    }
  }

  /* ----------------------------------------------------------------- dialog */

  /** Build a name from the trigger ("size chart") and the popup's product line. */
  function dialogName(el) {
    var trigger = lastTrigger || document.querySelector(TRIGGER_SELECTOR);
    var label = trigger ? (trigger.textContent || '').trim() : '';
    var topText = el.querySelector('.ccpops-popup__content__top-text');
    var subject = topText ? (topText.textContent || '').trim() : '';

    if (label && subject) return label + ': ' + subject;
    return label || subject || 'Size chart';
  }

  function patchDialog(el) {
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', dialogName(el));

    var close = el.querySelector(CLOSE_SELECTOR);
    if (close && !marked(close, 'close')) {
      close.setAttribute('role', 'button');
      if (!close.getAttribute('aria-label')) {
        close.setAttribute('aria-label', 'Close size chart');
      }
      close.addEventListener('keydown', function (event) {
        if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          close.click();
        }
      });
    }
  }

  function closeDialog() {
    var el = veil();
    var close = el && el.querySelector(CLOSE_SELECTOR);
    if (close) close.click();
  }

  function onKeydown(event) {
    var el = veil();
    if (!isOpen(el)) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    // Trap Tab inside the dialog; without this, focus walks into the page
    // behind the veil, which is still fully interactive.
    var items = visibleFocusables(el);
    if (!items.length) {
      event.preventDefault();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    // Focus starts on the dialog container itself, which is not in `items`.
    // Without this case Shift+Tab would walk backwards out of the dialog.
    if (active === el) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && (active === first || !el.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !el.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function onOpen(el) {
    patchDialog(el);

    // Focus the dialog itself rather than the close button, so the whole
    // dialog name and contents are announced on entry.
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus();

    setExpanded(true);
  }

  function onClose() {
    setExpanded(false);
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  }

  /* ---------------------------------------------------------------- observe */

  function syncOpenState() {
    var el = veil();
    var open = isOpen(el);
    if (open === wasOpen) return;
    wasOpen = open;
    if (open) onOpen(el);
    else onClose();
  }

  var veilObserved = null;

  function apply() {
    var triggers = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < triggers.length; i++) patchTrigger(triggers[i]);

    var el = veil();
    if (el && el !== veilObserved) {
      veilObserved = el;
      // The app toggles the modal via an inline style attribute.
      new MutationObserver(syncOpenState).observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      syncOpenState();
    }
  }

  function init() {
    apply();
    document.addEventListener('keydown', onKeydown, true);
    new MutationObserver(apply).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
